import axios from 'axios';
import { getListingImageUploadUrl, getAvatarUploadUrl } from '@/api/upload';
import i18n from '@/i18n';

export const validateImage = async (file) => {
  // Check file type
  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
  if (!acceptedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: i18n.t('validation.image.type_invalid')
    };
  }

  // Check file size (maximum 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: i18n.t('validation.image.size_too_large')
    };
  }

  // Check image dimensions
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const { width, height } = img;
      
      if (width < 600 || height < 600 || width > 2500 || height > 2500) {
        resolve({
          isValid: false,
          error: i18n.t('validation.image.dimensions_invalid')
        });
      } else {
        resolve({ isValid: true, error: null });
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve({
        isValid: false,
        error: i18n.t('validation.image.invalid_file')
      });
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export const validateAvatar = (file) => {
  // Only check file type
  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
  if (!acceptedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: i18n.t('validation.image.type_invalid')
    };
  }
  
  return { isValid: true, error: null };
};

export const getUploadUrl = async (fileInfo) => {
  try {
    return await getListingImageUploadUrl(fileInfo);
  } catch (error) {
    throw error;
  }
};

export const getAvatarUrl = async (fileInfo) => {
  try {
    return await getAvatarUploadUrl(fileInfo);
  } catch (error) {
    throw error;
  }
};

export const uploadFile = async (url, method, file) => {
  try {
    const response = await axios({
      url,
      method,
      data: file,
      headers: {
        'Content-Type': file.type,
        'Content-Length': file.size.toString(),
        'x-amz-meta-filename': encodeURIComponent(file.name)
      },
    });
    
    return response;
  } catch (error) {
    throw error;
  }
}; 

const buildCdnFitUrl = (url, fitSegment) => {
  if (!url || typeof url !== 'string') return url;

  // Fast-path: already processed
  if (url.includes('/image/fit-in/')) return url;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  if (parsed.hostname !== 'cdn.dondoncard.com') return url;

  const path = parsed.pathname || '';
  if (path.startsWith('/image/fit-in/')) return url;

  const newPath = `${fitSegment}${path}`;
  return `${parsed.protocol}//${parsed.host}${newPath}${parsed.search}${parsed.hash}`;
};

export const getProductThumbnailUrl = (url) =>
  buildCdnFitUrl(url, '/image/fit-in/300x400/filters:format(webp)');

export const getAvatarThumbnailUrl = (url) =>
  buildCdnFitUrl(url, '/image/fit-in/48x48/filters:format(webp)');
